<template>
  <div class="container-fluid mt-4 px-3 px-md-4">
    <DrawdownGuardBanner :guard="ddGuard" />

    <div class="d-flex justify-content-between align-items-start gap-2 mb-4 flex-wrap">
      <div>
        <h3 class="fw-bold text-dark mb-1">Portfolio</h3>
        <p class="text-muted mb-0">Real-time overview of your holdings</p>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap justify-content-end">
        <DrawdownGuardBadge :guard="ddGuard" />
        <span class="badge bg-light text-dark border">
          <i class="fas fa-sync-alt me-1"></i> Live
        </span>
      </div>
    </div>

    <div class="row g-3 mb-4 d-none d-md-flex">
      <div class="col">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-muted text-uppercase small fw-bold mb-2">Total Value</h6>
            <h3 class="fw-bold text-dark mb-0">${{ totalPortfolioValue.toLocaleString(undefined, {minimumFractionDigits: 2}) }}</h3>
          </div>
        </div>
      </div>
      <div class="col">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-muted text-uppercase small fw-bold mb-2">Cash Balance</h6>
            <h3 class="fw-bold text-dark mb-0">${{ cash_balance.toLocaleString(undefined, {minimumFractionDigits: 2}) }}</h3>
          </div>
        </div>
      </div>
      <div class="col">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-muted text-uppercase small fw-bold mb-2">Total Gain/Loss</h6>
            <h3 :class="['fw-bold mb-0', total_gain_loss >= 0 ? 'text-success' : 'text-danger']">
              {{ total_gain_loss >= 0 ? '+' : '' }}${{ total_gain_loss.toLocaleString(undefined, {minimumFractionDigits: 2}) }}
            </h3>
          </div>
        </div>
      </div>
      <div class="col">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-muted text-uppercase small fw-bold mb-2">Total Return</h6>
            <h3 :class="['fw-bold mb-0', total_return_pct >= 0 ? 'text-success' : 'text-danger']">
              <i :class="['fas', total_return_pct >= 0 ? 'fa-arrow-up' : 'fa-arrow-down', 'me-1 small']"></i>
              {{ Math.abs(total_return_pct).toFixed(2) }}%
            </h3>
          </div>
        </div>
      </div>
      <div class="col">
        <div class="card shadow-sm border-0 h-100">
          <div class="card-body">
            <h6 class="text-muted text-uppercase small fw-bold mb-2">Realized P&amp;L</h6>
            <h3 :class="['fw-bold mb-0', realized_pnl >= 0 ? 'text-success' : 'text-danger']">
              {{ realized_pnl >= 0 ? '+' : '' }}${{ realized_pnl.toLocaleString(undefined, {minimumFractionDigits: 2}) }}
            </h3>
          </div>
        </div>
      </div>
    </div>

    <div class="card shadow-sm border-0 mb-4 d-md-none">
      <div class="card-body p-0">
        <ul class="list-unstyled m-0">
          <li class="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
            <span class="text-muted text-uppercase small fw-bold">Total Value</span>
            <span class="fw-bold text-dark">${{ totalPortfolioValue.toLocaleString(undefined, {minimumFractionDigits: 2}) }}</span>
          </li>
          <li class="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
            <span class="text-muted text-uppercase small fw-bold">Cash Balance</span>
            <span class="fw-bold text-dark">${{ cash_balance.toLocaleString(undefined, {minimumFractionDigits: 2}) }}</span>
          </li>
          <li class="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
            <span class="text-muted text-uppercase small fw-bold">Total Gain/Loss</span>
            <span :class="['fw-bold', total_gain_loss >= 0 ? 'text-success' : 'text-danger']">
              {{ total_gain_loss >= 0 ? '+' : '' }}${{ total_gain_loss.toLocaleString(undefined, {minimumFractionDigits: 2}) }}
            </span>
          </li>
          <li class="d-flex justify-content-between align-items-center px-3 py-3 border-bottom">
            <span class="text-muted text-uppercase small fw-bold">Total Return</span>
            <span :class="['fw-bold', total_return_pct >= 0 ? 'text-success' : 'text-danger']">
              <i :class="['fas', total_return_pct >= 0 ? 'fa-arrow-up' : 'fa-arrow-down', 'me-1 small']"></i>
              {{ Math.abs(total_return_pct).toFixed(2) }}%
            </span>
          </li>
          <li class="d-flex justify-content-between align-items-center px-3 py-3">
            <span class="text-muted text-uppercase small fw-bold">Realized P&amp;L</span>
            <span :class="['fw-bold', realized_pnl >= 0 ? 'text-success' : 'text-danger']">
              {{ realized_pnl >= 0 ? '+' : '' }}${{ realized_pnl.toLocaleString(undefined, {minimumFractionDigits: 2}) }}
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div class="card shadow-sm border-0 rounded-3">
      <div class="card-header bg-white py-3">
        <h5 class="mb-0 fw-bold">Stock Positions</h5>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive d-none d-md-block">
          <table class="table table-hover align-middle mb-0">
            <thead class="bg-light text-secondary">
              <tr>
                <th class="ps-4 py-3" style="width: 40px;"></th>
                <th class="py-3 fw-semibold text-uppercase small">Ticker</th>
                <th class="py-3" style="width: 120px;"></th>
                <th class="py-3 fw-semibold text-uppercase small">Company</th>
                <th class="py-3 fw-semibold text-uppercase small text-center">Index</th>
                <th class="py-3 fw-semibold text-uppercase small text-end">Shares</th>
                <th class="py-3 fw-semibold text-uppercase small text-end">Avg Price</th>
                <th class="py-3 fw-semibold text-uppercase small text-end">Current</th>
                <th class="py-3 fw-semibold text-uppercase small text-end">Value</th>
                <th class="py-3 fw-semibold text-uppercase small text-end">P/L ($)</th>
                <th class="py-3 fw-semibold text-uppercase small text-end pe-4">Return</th>
                <th class="py-3 fw-semibold text-uppercase small text-end pe-4">Action</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="position in positions" :key="position.symbol">
                <tr>
                  <td class="ps-4">
                    <button class="btn btn-sm btn-link text-decoration-none p-0 text-dark" @click="toggleRow(position.symbol)">
                       <i :class="['fas', isExpanded(position.symbol) ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
                    </button>
                  </td>
                  <td class="fw-bold">
                    <a :href="`https://finance.yahoo.com/quote/${position.symbol}/`" target="_blank" class="text-primary text-decoration-none">
                      {{ position.symbol }} <i class="fas fa-external-link-alt small ms-1" style="font-size: 0.7em;"></i>
                    </a>
                  </td>
                  <td>
                    <TickerActionButtons :ticker="position.symbol" />
                  </td>
                  <td class="text-muted">{{ getCompanyName(position.symbol) }}</td>
                  <td class="text-center">
                    <span v-if="getSourceIndex(position.symbol)" class="badge bg-light text-dark border">
                      {{ getSourceIndex(position.symbol) }}
                    </span>
                    <span v-else class="text-muted small">—</span>
                  </td>
                  <td class="text-end fw-medium">{{ position.display_quantity }}</td>
                  <td class="text-end text-muted">{{ formatPrice(position.avg_price) }}</td>
                  <td class="text-end fw-bold">{{ formatPrice(position.current_price) }}</td>
                  <td class="text-end fw-bold">
                    <span :class="['badge', 'rounded-pill', 'me-2', sideBadgeClass(position.side)]" style="font-size: 0.7em;">
                      {{ position.side === 'SHORT' ? 'SHORT' : 'LONG' }}
                    </span>
                    {{ formatCurrency(position.market_exposure) }}
                  </td>
                  <td class="text-end">
                    <span :class="['badge', pnlBadgeClass(position.profit_loss)]">
                      {{ formatSignedNumber(position.profit_loss) }}
                    </span>
                  </td>
                  <td class="text-end pe-4">
                    <span :class="['fw-bold', pnlTextClass(position.return_pct)]">
                      {{ formatPercent(position.return_pct) }}
                    </span>
                  </td>
                  <td class="text-end pe-4">
                    <SellButton
                      :ticker="position.symbol"
                      :quantity="position.quantity"
                      :currentPrice="position.current_price"
                      :side="position.side"
                      @sold="fetchPositions"
                    />
                  </td>
                </tr>
                <tr v-if="isExpanded(position.symbol)" class="bg-light">
                  <td colspan="12" class="p-4">
                    <TradeHistoryCard
                      :symbol="position.symbol"
                      :trades="getTrades(position.symbol)"
                      :loading="loadingTrades[position.symbol] || false"
                    />
                  </td>
                </tr>
              </template>
              <tr v-if="positions.length === 0">
                <td colspan="12" class="text-center py-5 text-muted">
                  <i class="fas fa-folder-open fa-2x mb-3 opacity-25"></i>
                  <p class="mb-0">Your portfolio is currently empty.</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-md-none">
          <div v-if="positions.length === 0" class="text-center py-5 text-muted">
            <i class="fas fa-folder-open fa-2x mb-3 opacity-25"></i>
            <p class="mb-0">Your portfolio is currently empty.</p>
          </div>
          <ul v-else class="list-unstyled m-0 p-3 d-flex flex-column gap-2">
            <li
              v-for="position in positions"
              :key="position.symbol"
              class="border rounded-3 bg-white overflow-hidden"
            >
              <div
                class="d-flex align-items-center gap-2 px-3 py-2"
                role="button"
                @click="toggleRow(position.symbol)"
              >
                <a
                  :href="`https://finance.yahoo.com/quote/${position.symbol}/`"
                  target="_blank"
                  class="fw-bold text-primary text-decoration-none"
                  @click.stop
                >
                  {{ position.symbol }}
                  <i class="fas fa-external-link-alt ms-1" style="font-size: 0.7em;"></i>
                </a>
                <span @click.stop>
                  <TickerActionButtons :ticker="position.symbol" />
                </span>
                <span
                  :class="['badge', 'rounded-pill', sideBadgeClass(position.side)]"
                  style="font-size: 0.7em;"
                >{{ position.side === 'SHORT' ? 'SHORT' : 'LONG' }}</span>
                <span v-if="getSourceIndex(position.symbol)" class="badge bg-light text-dark border" style="font-size: 0.7em;">
                  {{ getSourceIndex(position.symbol) }}
                </span>
                <i
                  :class="[
                    'fas',
                    'ms-auto',
                    'text-muted',
                    isExpanded(position.symbol) ? 'fa-chevron-down' : 'fa-chevron-right',
                  ]"
                ></i>
              </div>
              <div class="d-flex justify-content-between align-items-end px-3 py-2 border-top">
                <div>
                  <div class="text-muted text-uppercase small fw-bold">Value</div>
                  <div class="fw-bold">
                    {{ formatCurrency(position.market_exposure) }}
                  </div>
                </div>
                <div class="text-end">
                  <div class="text-muted text-uppercase small fw-bold">Return</div>
                  <div :class="['fw-bold', pnlTextClass(position.return_pct)]">
                    {{ formatPercent(position.return_pct) }}
                  </div>
                </div>
              </div>
              <div class="d-flex flex-wrap align-items-center gap-2 px-3 pb-2 small text-muted">
                <span>{{ position.display_quantity }} sh</span>
                <span class="opacity-50">·</span>
                <span>avg {{ formatPrice(position.avg_price) }}</span>
                <span class="opacity-50">·</span>
                <span>now {{ formatPrice(position.current_price) }}</span>
                <span class="opacity-50">·</span>
                <span :class="pnlTextClass(position.profit_loss)">
                  P/L {{ formatSignedNumber(position.profit_loss) }}
                </span>
              </div>
              <div v-if="isExpanded(position.symbol)" class="bg-light p-3 border-top">
                <TradeHistoryCard
                  :symbol="position.symbol"
                  :trades="getTrades(position.symbol)"
                  :loading="loadingTrades[position.symbol] || false"
                />
                <div class="mt-3 d-grid">
                  <SellButton
                    :ticker="position.symbol"
                    :quantity="position.quantity"
                    :currentPrice="position.current_price"
                    :side="position.side"
                    @sold="fetchPositions"
                  />
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { h } from 'vue';
import { fetchPortfolio, fetchTrades, validateTicker, fetchRealizedPnl, fetchWatchlist } from '../services/api';
import TickerActionButtons from '../components/TickerActionButtons.vue';
import SellButton from '../components/SellButton.vue';
import DrawdownGuardBanner from '../components/DrawdownGuardBanner.vue';
import DrawdownGuardBadge from '../components/DrawdownGuardBadge.vue';
import { useDrawdownGuard } from '../composables/useDrawdownGuard';

const TradeHistoryCard = {
  name: 'TradeHistoryCard',
  props: {
    symbol: { type: String, required: true },
    trades: { type: Array, default: null },
    loading: { type: Boolean, default: false },
  },
  methods: {
    formatTradeDate(dateString) {
      if (!dateString) return '';
      let ts = dateString;
      if (typeof ts === 'string' && !ts.endsWith('Z') && ts.includes('T')) {
        ts += 'Z';
      }
      return new Date(ts).toLocaleString();
    },
  },
  render() {
    let body;
    if (this.loading) {
      body = h('div', { class: 'text-center py-3' }, [
        h('div', { class: 'spinner-border spinner-border-sm text-primary', role: 'status' }, [
          h('span', { class: 'visually-hidden' }, 'Loading...'),
        ]),
      ]);
    } else if (!this.trades || this.trades.length === 0) {
      body = h(
        'div',
        { class: 'text-center py-3 text-muted small' },
        'No trade history found.',
      );
    } else {
      body = h('table', { class: 'table table-sm table-borderless mb-0' }, [
        h('thead', { class: 'text-secondary border-bottom' }, [
          h('tr', null, [
            h('th', { class: 'ps-3 py-2 small' }, 'Date'),
            h('th', { class: 'py-2 small' }, 'Type'),
            h('th', { class: 'py-2 small text-end' }, 'Qty'),
            h('th', { class: 'py-2 small text-end' }, 'Price'),
            h('th', { class: 'py-2 small text-end' }, 'Total'),
            h('th', { class: 'py-2 small text-end pe-3' }, 'Trace ID'),
          ]),
        ]),
        h(
          'tbody',
          null,
          this.trades.map((trade) =>
            h('tr', { key: trade.id }, [
              h('td', { class: 'ps-3 text-muted small' }, this.formatTradeDate(trade.timestamp)),
              h('td', null, [
                h(
                  'span',
                  {
                    class: [
                      'badge',
                      trade.side === 'BUY'
                        ? 'bg-success-subtle text-success'
                        : 'bg-danger-subtle text-danger',
                    ],
                    style: 'font-size: 0.75em;',
                  },
                  trade.side,
                ),
              ]),
              h('td', { class: 'text-end small' }, String(trade.qty)),
              h(
                'td',
                { class: 'text-end small' },
                `$${parseFloat(trade.price).toFixed(2)}`,
              ),
              h(
                'td',
                { class: 'text-end small fw-bold' },
                `$${(trade.qty * trade.price).toFixed(2)}`,
              ),
              h(
                'td',
                {
                  class: 'text-end pe-3 small text-muted font-monospace',
                  title: trade.e2etransactionid,
                },
                trade.e2etransactionid
                  ? `${trade.e2etransactionid.substring(0, 8)}...`
                  : '-',
              ),
            ]),
          ),
        ),
      ]);
    }
    return h('div', { class: 'card border-0 shadow-sm' }, [
      h('div', { class: 'card-header bg-white border-bottom py-2' }, [
        h(
          'h6',
          { class: 'mb-0 text-muted small text-uppercase fw-bold' },
          `Trade History - ${this.symbol}`,
        ),
      ]),
      h('div', { class: 'card-body p-0' }, [body]),
    ]);
  },
};

export default {
  name: 'Positions',
  components: { TickerActionButtons, SellButton, TradeHistoryCard, DrawdownGuardBanner, DrawdownGuardBadge },
  setup() {
    // Live drawdown-guard state, polled off the same /portfolio/ endpoint.
    const { guard: ddGuard } = useDrawdownGuard();
    return { ddGuard };
  },
  data() {
    return {
      positions: [],
      cash_balance: 0,
      realized_pnl: 0,
      net_liquidation: 0,
      total_gain_loss: 0,
      total_return_pct: 0,
      expandedRows: [],
      tradesData: {},
      loadingTrades: {},
      companyNames: {},
      sourceIndexByTicker: {},
      pollingInterval: null,
      layoutMode: null,
      mediaQueryList: null,
      handleLayoutChange: null,
    }
  },
  created() {
    this.fetchingNames = new Set();
  },
  computed: {
    totalPortfolioValue() {
      return this.net_liquidation;
    }
  },
  methods: {
    async fetchPositions() {
      try {
        const [portfolioData, realizedData] = await Promise.all([
          fetchPortfolio(),
          fetchRealizedPnl()
        ]);
        if (portfolioData) {
            this.positions = portfolioData.positions || [];
            this.cash_balance = portfolioData.cash_balance || 0;
            this.net_liquidation = portfolioData.net_liquidation || 0;
            this.total_gain_loss = portfolioData.total_gain_loss || 0;
            this.total_return_pct = portfolioData.total_return_pct || 0;

            this.positions.forEach(position => {
              if (!this.companyNames[position.symbol]) {
                this.fetchCompanyName(position.symbol);
              }
            });
        }
        if (realizedData) {
          this.realized_pnl = realizedData.realized_pnl || 0;
        }
      } catch (error) {
        console.error('Error fetching positions:', error);
      }
    },
    async fetchSourceIndexes() {
      try {
        const data = await fetchWatchlist();
        const map = {};
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        // First occurrence wins so we keep the earliest index a ticker
        // qualified under for the current trading day.
        for (const entry of entries) {
          if (entry?.ticker && entry?.index && !map[entry.ticker]) {
            map[entry.ticker] = entry.index;
          }
        }
        this.sourceIndexByTicker = map;
      } catch (error) {
        console.warn('[Positions] Failed to fetch watchlist for source-index mapping:', error);
      }
    },
    getSourceIndex(symbol) {
      return this.sourceIndexByTicker[symbol] || '';
    },
    async fetchCompanyName(symbol) {
      if (this.fetchingNames.has(symbol)) return;
      this.fetchingNames.add(symbol);
      try {
        const data = await validateTicker(symbol);
        if (data && data.valid && data.name) {
          this.companyNames[symbol] = data.name;
        }
      } catch (error) {
        console.error(`Error fetching company name for ${symbol}:`, error);
      } finally {
        this.fetchingNames.delete(symbol);
      }
    },
    getCompanyName(symbol) {
      return this.companyNames[symbol] || symbol;
    },
    isExpanded(symbol) {
      return this.expandedRows.includes(symbol);
    },
    async toggleRow(symbol) {
      const index = this.expandedRows.indexOf(symbol);
      if (index > -1) {
        this.expandedRows.splice(index, 1);
        return;
      }
      this.expandedRows.push(symbol);
      if (this.layoutMode === 'mobile') {
        console.info('positions_view.position_expanded', {
          symbol,
          mode: this.layoutMode,
        });
      }
      if (!this.tradesData[symbol]) {
        await this.loadTradesForSymbol(symbol);
      }
    },
    async loadTradesForSymbol(symbol) {
      this.loadingTrades[symbol] = true;
      try {
        const data = await fetchTrades(1, 100, symbol);
        const trades = data.trades || [];
        this.tradesData[symbol] = trades.filter(t => t.ticker === symbol);
      } catch (error) {
        console.error(`Error fetching trades for ${symbol}:`, error);
      } finally {
        this.loadingTrades[symbol] = false;
      }
    },
    getTrades(symbol) {
      return this.tradesData[symbol];
    },
    formatCurrency(value) {
      const v = Number(value) || 0;
      return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    },
    formatPrice(value) {
      const v = Number(value) || 0;
      return `$${v.toFixed(2)}`;
    },
    formatSignedNumber(value) {
      const v = Number(value) || 0;
      return `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
    },
    formatPercent(value) {
      const v = Number(value) || 0;
      return `${v.toFixed(2)}%`;
    },
    pnlBadgeClass(value) {
      return (value || 0) >= 0
        ? 'bg-success-subtle text-success'
        : 'bg-danger-subtle text-danger';
    },
    pnlTextClass(value) {
      return (value || 0) >= 0 ? 'text-success' : 'text-danger';
    },
    sideBadgeClass(side) {
      return (side || '').toUpperCase() === 'SHORT'
        ? 'bg-warning text-dark'
        : 'bg-info text-dark';
    },
  },
  mounted() {
    this.fetchPositions();
    this.fetchSourceIndexes();
    this.pollingInterval = setInterval(this.fetchPositions, 3600000);

    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      this.mediaQueryList = window.matchMedia('(min-width: 768px)');
      this.handleLayoutChange = (event) => {
        const next = event.matches ? 'desktop' : 'mobile';
        if (next === this.layoutMode) return;
        this.layoutMode = next;
        console.info('positions_view.layout_mode_changed', {
          mode: next,
          viewport_width: window.innerWidth,
        });
      };
      this.handleLayoutChange(this.mediaQueryList);
      this.mediaQueryList.addEventListener('change', this.handleLayoutChange);
    }
  },
  beforeUnmount() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    if (this.mediaQueryList && this.handleLayoutChange) {
      this.mediaQueryList.removeEventListener('change', this.handleLayoutChange);
    }
  }
}
</script>

<style scoped>
/* Reuse modern styles */
.bg-success-subtle {
  background-color: #d1e7dd !important;
  color: #0f5132 !important;
}
.bg-danger-subtle {
  background-color: #f8d7da !important;
  color: #842029 !important;
}
.card {
  overflow: hidden;
}
th {
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}
td {
  font-size: 0.9rem;
}
</style>
